import {
  createLegacyPendingSuppression,
  createLocalizationContext,
  createLocalizationIntent,
  createLocalizationPromptState,
  formatLocalizationStatusLabel,
  getLocalizationDecision,
  getLocalizationReturnTo,
  getLocalizationSelection,
  getPromptCoordinator,
  isLocalizationContextCoherent,
  isLocalizationSelectionAlreadyApplied,
  isFreshState,
  isStorageWritable,
  migrateLocalizationState,
  normalizeCountry,
  normalizeCurrency,
  normalizeLanguage,
  readJsonStateResult,
  reconcileLocalizationIntent,
  writeJsonState
} from '@theme/storefront-prompts';

(function () {
  if (window.HDTLocalizationSelector) {
    return;
  }

  window.HDTLocalizationSelector = true;

  var HDT_LOCALIZATION_BUILD = 'authority-v4.2';
  var PROMPT_STATE_KEY = 'hdt_localization_prompt_v4';
  var INTENT_KEY = 'hdt_localization_intent_v4';
  var LEGACY_STATE_KEY_V3 = 'hdt_localization_state_v3';
  var LEGACY_STATE_KEY_V2 = 'hdt_localization_state_v2';
  var LEGACY_CHOICE_KEY = 'hdt_localization_choice_v1';
  var CONFIRMED_KEY = 'hdt_localization_confirmed_v1';
  var DISMISS_KEY = 'hdt_localization_gate_dismissed_v1';
  var CONFIRM_TTL = 90 * 24 * 60 * 60 * 1000;
  var DISMISS_TTL = 7 * 24 * 60 * 60 * 1000;
  var UNRESOLVED_SUPPRESSION_TTL = 60 * 60 * 1000;
  var INTENT_TTL = 10 * 60 * 1000;
  var root = document.querySelector('[data-hdt-localization]');
  var promptCoordinator = getPromptCoordinator();

  if (!root) {
    if (promptCoordinator) {
      promptCoordinator.complete('localization', { source: 'automatic', outcome: 'skipped' });
    }
    return;
  }

  root.dataset.localizationControllerBuild = HDT_LOCALIZATION_BUILD;

  var dialog = root.querySelector('#HdtLocalizationDialog');
  var form = root.querySelector('#HdtLocalizationForm');
  var countrySelect = root.querySelector('[data-localization-country]');
  var countryFlag = root.querySelector('[data-localization-country-flag]');
  var languageSelect = root.querySelector('[data-localization-language]');
  var configScript = root.querySelector('[data-hdt-localization-config]');
  var title = root.querySelector('[data-localization-title]');
  var description = root.querySelector('[data-localization-description]');
  var recommendation = root.querySelector('[data-localization-recommendation]');
  var localizationStatus = root.querySelector('[data-localization-status]');
  var dismissLabel = root.querySelector('[data-localization-dismiss-label]');
  var submitLabel = root.querySelector('[data-localization-submit-label]');
  var countryCustom = root.querySelector('[data-localization-custom="country"]');
  var languageCustom = root.querySelector('[data-localization-custom="language"]');
  var countryCustomTriggerFlag = root.querySelector('[data-localization-country-trigger-flag]');
  var lastFocused = null;
  var openedAsRecommendation = false;
  var activeRecommendation = null;
  var pendingPromptOutcome = '';
  var activePromptSource = 'manual';
  var promptCompleted = false;
  var promptSuspended = false;
  var manualInteractionStarted = false;
  var memoryState = null;
  var unresolvedIntent = null;
  var controllerStateInitialized = false;
  var localStorageRef = null;
  var sessionStorageRef = null;
  var storageAvailable = false;
  var config = {};

  try {
    config = JSON.parse(configScript ? configScript.textContent : '{}');
  } catch (error) {
    config = {};
  }

  try {
    localStorageRef = window.localStorage;
    sessionStorageRef = window.sessionStorage;
  } catch (error) {
    localStorageRef = null;
    sessionStorageRef = null;
  }

  var localStorageWritable = isStorageWritable(localStorageRef);
  var sessionStorageWritable = isStorageWritable(sessionStorageRef);
  storageAvailable = localStorageWritable && sessionStorageWritable;

  var allLanguages = Array.isArray(config.languages) ? config.languages : [];
  var languageLabels = allLanguages.reduce(function (labels, language) {
    labels[normalizeLanguage(language.code)] = language.name;
    return labels;
  }, {});
  var countries = countrySelect ? Array.prototype.map.call(countrySelect.options, function (option) {
    var label = String(option.textContent || '').trim();
    return {
      code: normalizeCountry(option.value),
      currency: normalizeCurrency(option.dataset.countryCurrency),
      name: label.replace(/\s+\([A-Z]{3}\)\s*$/, ''),
      label: label,
      flagUrl: option.dataset.countryFlag || '',
      languages: String(option.dataset.countryLanguages || '').split(',').filter(Boolean).map(function (code) {
        code = normalizeLanguage(code);
        return { code: code, name: languageLabels[code] || code };
      })
    };
  }) : [];
  var currentContext = createLocalizationContext({
    country: config.currentCountry,
    language: config.currentLanguage,
    countryCurrency: config.currentCountryCurrency,
    cartCurrency: config.cartCurrency
  });
  var customOptionsReady = false;

  var isVisible = function (element) {
    if (!element || element === dialog) {
      return false;
    }
    var style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
      return false;
    }
    var rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  };

  var findCountry = function (code) {
    code = String(code || '').toUpperCase();
    for (var i = 0; i < countries.length; i += 1) {
      if (String(countries[i].code).toUpperCase() === code) {
        return countries[i];
      }
    }
    return null;
  };

  var updateCountryFlag = function (countryCode) {
    if (!countryFlag) {
      return;
    }

    var country = findCountry(countryCode);
    if (country && country.flagUrl) {
      countryFlag.src = country.flagUrl;
      countryFlag.hidden = false;
      return;
    }

    countryFlag.hidden = true;
  };

  var normalizeCountryCode = function (value) {
    value = normalizeCountry(value);
    return value.length === 2 ? value : '';
  };

  var getLanguagesForCountry = function (countryCode) {
    var country = findCountry(countryCode);
    if (country && Array.isArray(country.languages) && country.languages.length) {
      return country.languages;
    }
    return allLanguages;
  };

  var hasLanguage = function (languages, code) {
    code = String(code || '').toLowerCase();
    return languages.some(function (language) {
      return String(language.code || '').toLowerCase() === code;
    });
  };

  var normalizeLanguageCode = function (value) {
    return normalizeLanguage(value);
  };

  var formatCountryLabel = function (country) {
    if (!country) {
      return '';
    }
    return country.label || country.name;
  };

  var getText = function (element, key) {
    return element && element.dataset ? element.dataset[key] || '' : '';
  };

  var setText = function (element, value) {
    if (element && value) {
      element.textContent = value;
    }
  };

  var clearLocalizationStatus = function () {
    if (!localizationStatus) {
      return;
    }
    localizationStatus.textContent = '';
    localizationStatus.hidden = true;
  };

  var showUnresolvedStatus = function () {
    clearLocalizationStatus();
    if (!localizationStatus || !unresolvedIntent || unresolvedIntent.status !== 'unresolved') {
      return;
    }

    if (!isFreshState(unresolvedIntent, Date.now())) {
      unresolvedIntent = null;
      removeStoredValue(sessionStorageRef, INTENT_KEY);
      return;
    }

    var target = unresolvedIntent.target || {};
    var targetMatchesCurrent = isLocalizationContextCoherent({
      context: currentContext,
      availability: getAvailability()
    })
      && normalizeCountry(target.country) === currentContext.country
      && normalizeLanguage(target.language) === currentContext.language
      && normalizeCurrency(target.currency) === currentContext.countryCurrency
      && normalizeCurrency(target.currency) === currentContext.cartCurrency;
    if (targetMatchesCurrent) {
      unresolvedIntent = null;
      return;
    }

    var targetCountry = findCountry(target.country);
    var observedCountry = findCountry(currentContext.country);
    var targetLanguage = findLanguage(getLanguagesForCountry(target.country), target.language)
      || findLanguage(allLanguages, target.language);
    var observedLanguage = findLanguage(getLanguagesForCountry(currentContext.country), currentContext.language)
      || findLanguage(allLanguages, currentContext.language);
    var targetLabel = formatLocalizationStatusLabel({
      countryLabel: formatCountryLabel(targetCountry) || normalizeCountry(target.country),
      languageLabel: targetLanguage ? targetLanguage.name : normalizeLanguage(target.language)
    });
    var observedLabel = formatLocalizationStatusLabel({
      countryLabel: formatCountryLabel(observedCountry) || currentContext.country,
      languageLabel: observedLanguage ? observedLanguage.name : currentContext.language
    });
    var template = getText(localizationStatus, 'unresolvedText');
    var unresolvedGenericText = getText(localizationStatus, 'unresolvedGenericText');
    if (!targetLabel || !observedLabel) {
      return;
    }

    if (targetLabel === observedLabel) {
      if (!unresolvedGenericText) {
        return;
      }
      localizationStatus.textContent = unresolvedGenericText;
      localizationStatus.hidden = false;
      return;
    }

    if (!template) {
      return;
    }

    localizationStatus.textContent = template
      .replace('__TARGET__', targetLabel)
      .replace('__CURRENT__', observedLabel);
    localizationStatus.hidden = false;
  };

  var resetDialogCopy = function () {
    root.classList.remove('hdt-localization--confirmation');
    setText(title, getText(title, 'defaultText'));
    setText(description, getText(description, 'defaultText'));
    setText(dismissLabel, getText(dismissLabel, 'defaultText'));
    setText(submitLabel, getText(submitLabel, 'defaultText'));
    if (recommendation) {
      recommendation.hidden = true;
    }
  };

  var setConfirmationCopy = function (state) {
    var countryName = state.country && state.country.name ? state.country.name : '';
    var titleTemplate = countryName ? getText(title, 'confirmationCountry') : getText(title, 'confirmationDefault');

    root.classList.add('hdt-localization--confirmation');
    setText(title, titleTemplate ? titleTemplate.replace('__COUNTRY__', countryName) : getText(title, 'confirmationDefault'));
    setText(description, getText(description, 'confirmationText'));
    setText(dismissLabel, getText(dismissLabel, 'confirmationText'));
    setText(submitLabel, getText(submitLabel, 'confirmationText'));
    if (recommendation) {
      recommendation.hidden = true;
    }
  };

  var setRecommendationCopy = function (state) {
    root.classList.remove('hdt-localization--confirmation');
    var templateKey = state.type === 'combined' ? 'recommendationCombined' : state.type === 'language' ? 'recommendationLanguage' : 'recommendationCountry';
    var template = getText(description, templateKey);
    var countryName = state.country && state.country.name ? state.country.name : '';
    var languageName = state.language && state.language.name ? state.language.name : '';

    setText(title, getText(title, 'recommendationText'));
    setText(
      description,
      template
        ? template.replace('__COUNTRY__', countryName).replace('__LANGUAGE__', languageName)
        : ''
    );
    setText(dismissLabel, getText(dismissLabel, 'recommendationText'));
    setText(submitLabel, getText(submitLabel, 'recommendationText'));
    if (recommendation) {
      recommendation.hidden = false;
    }
  };

  var findLanguage = function (languages, code) {
    code = String(code || '').toLowerCase();
    for (var i = 0; i < languages.length; i += 1) {
      if (String(languages[i].code || '').toLowerCase() === code) {
        return languages[i];
      }
    }
    return null;
  };

  var getCustomParts = function (custom) {
    return {
      trigger: custom ? custom.querySelector('[data-localization-custom-trigger]') : null,
      value: custom ? custom.querySelector('[data-localization-custom-value]') : null,
      list: custom ? custom.querySelector('[data-localization-custom-list]') : null
    };
  };

  var updateCustomCountry = function (countryCode) {
    var country = findCountry(countryCode);
    var parts = getCustomParts(countryCustom);
    if (!country || !parts.value) {
      return;
    }

    parts.value.textContent = formatCountryLabel(country);
    if (countryCustomTriggerFlag && country.flagUrl) {
      countryCustomTriggerFlag.src = country.flagUrl;
      countryCustomTriggerFlag.hidden = false;
    }

    if (countryCustom) {
      countryCustom.querySelectorAll('[data-localization-country-option]').forEach(function (option) {
        option.setAttribute(
          'aria-selected',
          String(option.dataset.localizationCountryOption || '').toUpperCase() === String(country.code || '').toUpperCase() ? 'true' : 'false'
        );
      });
    }
  };

  var updateCustomLanguage = function (languageCode) {
    var languages = getLanguagesForCountry(countrySelect ? countrySelect.value : config.currentCountry);
    var language = findLanguage(languages, languageCode) || findLanguage(allLanguages, languageCode);
    var parts = getCustomParts(languageCustom);
    if (!language || !parts.value) {
      return;
    }

    parts.value.textContent = language.name;
    if (languageCustom) {
      languageCustom.querySelectorAll('[data-localization-language-option]').forEach(function (option) {
        option.setAttribute(
          'aria-selected',
          String(option.dataset.localizationLanguageOption || '').toLowerCase() === String(language.code || '').toLowerCase() ? 'true' : 'false'
        );
      });
    }
  };

  var renderLanguageCustomOptions = function (languages, selected) {
    var parts = getCustomParts(languageCustom);
    if (!parts.list || !customOptionsReady) {
      return;
    }

    parts.list.replaceChildren();
    languages.forEach(function (language) {
      var option = document.createElement('button');
      var label = document.createElement('span');
      option.type = 'button';
      option.className = 'hdt-localization__custom-option';
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', String(language.code).toLowerCase() === String(selected).toLowerCase() ? 'true' : 'false');
      option.setAttribute('data-localization-language-option', language.code);
      option.setAttribute('data-localization-option', '');
      label.textContent = language.name;
      option.append(label);
      parts.list.append(option);
    });
  };

  var ensureCustomOptions = function () {
    if (customOptionsReady) {
      return true;
    }

    try {
      var countryParts = getCustomParts(countryCustom);
      if (!countryParts.list) {
        return false;
      }

      if (!countryParts.list.children.length) {
        var fragment = document.createDocumentFragment();
        countries.forEach(function (country) {
          var option = document.createElement('button');
          var label = document.createElement('span');
          option.type = 'button';
          option.className = 'hdt-localization__custom-option';
          option.setAttribute('role', 'option');
          option.setAttribute('aria-selected', String(country.code).toUpperCase() === String(countrySelect.value).toUpperCase() ? 'true' : 'false');
          option.setAttribute('data-localization-country-option', country.code);
          option.setAttribute('data-localization-option', '');

          if (country.flagUrl) {
            var flag = document.createElement('img');
            flag.className = 'hdt-localization__custom-flag';
            flag.src = country.flagUrl;
            flag.alt = '';
            flag.width = 20;
            flag.height = 14;
            flag.loading = 'lazy';
            option.append(flag);
          }

          label.textContent = formatCountryLabel(country);
          option.append(label);
          fragment.append(option);
        });
        countryParts.list.append(fragment);
      }

      customOptionsReady = true;
      renderLanguageCustomOptions(
        getLanguagesForCountry(countrySelect ? countrySelect.value : config.currentCountry),
        languageSelect ? languageSelect.value : config.currentLanguage
      );
      updateCustomCountry(countrySelect ? countrySelect.value : config.currentCountry);
      updateCustomLanguage(languageSelect ? languageSelect.value : config.currentLanguage);
      root.classList.add('hdt-localization--enhanced');
      return true;
    } catch (error) {
      customOptionsReady = false;
      root.classList.remove('hdt-localization--enhanced');
      return false;
    }
  };

  var updateLanguageOptions = function (countryCode, preferredLanguage) {
    if (!languageSelect) {
      return;
    }

    var languages = getLanguagesForCountry(countryCode);
    var selected = preferredLanguage || languageSelect.value || config.currentLanguage;

    if (!hasLanguage(languages, selected)) {
      selected = languages[0] ? languages[0].code : '';
    }

    languageSelect.replaceChildren();
    languages.forEach(function (language) {
      var option = new Option(language.name, language.code);
      option.selected = String(language.code).toLowerCase() === String(selected).toLowerCase();
      languageSelect.append(option);
    });
    renderLanguageCustomOptions(languages, selected);
    updateCustomLanguage(selected);
  };

  var getReturnTo = function () {
    return getLocalizationReturnTo(window.location.href);
  };

  var setReturnTo = function () {
    var input = form ? form.querySelector('[name="return_to"]') : null;
    if (input) {
      input.value = getReturnTo();
    }
  };

  var getLanguageFromPath = function () {
    var segment = window.location.pathname.split('/').filter(Boolean)[0];
    if (!segment) {
      return '';
    }
    segment = segment.toLowerCase();
    for (var i = 0; i < allLanguages.length; i += 1) {
      var code = String(allLanguages[i].code || '').toLowerCase();
      if (code === segment || code.split('-')[0] === segment) {
        return allLanguages[i].code;
      }
    }
    return '';
  };

  var getBrowserLanguageMatch = function (languages) {
    var browserLanguages = [];
    if (window.navigator) {
      if (Array.isArray(window.navigator.languages)) {
        browserLanguages = window.navigator.languages;
      } else if (window.navigator.language) {
        browserLanguages = [window.navigator.language];
      }
    }

    for (var i = 0; i < browserLanguages.length; i += 1) {
      var prefix = normalizeLanguageCode(browserLanguages[i]).split('-')[0];
      if (!prefix) {
        continue;
      }
      for (var j = 0; j < languages.length; j += 1) {
        var code = normalizeLanguageCode(languages[j].code);
        if (code === prefix || code.split('-')[0] === prefix) {
          return languages[j];
        }
      }
    }
    return null;
  };

  var enterManualOnly = function () {
    storageAvailable = false;
    memoryState = null;
  };

  var readStoredResult = function (storage, key) {
    if (!storageAvailable) {
      return { status: 'unavailable', value: null };
    }
    var result = readJsonStateResult(storage, key);
    if (result.status === 'unavailable') {
      enterManualOnly();
    }
    return result;
  };

  var readStoredValue = function (storage, key) {
    var result = readStoredResult(storage, key);
    return result.status === 'ok' ? result.value : null;
  };

  var removeStoredValue = function (storage, key) {
    if (!storageAvailable) {
      return false;
    }
    try {
      storage.removeItem(key);
      return true;
    } catch (error) {
      enterManualOnly();
      return false;
    }
  };

  var persistPromptState = function (state) {
    if (!storageAvailable || !writeJsonState(localStorageRef, PROMPT_STATE_KEY, state)) {
      enterManualOnly();
      return false;
    }
    memoryState = state;
    return true;
  };

  var persistIntentState = function (state) {
    if (!storageAvailable || !writeJsonState(sessionStorageRef, INTENT_KEY, state)) {
      enterManualOnly();
      return false;
    }
    return true;
  };

  var createLegacyConfirmationState = function (value, ttl) {
    if (!value) {
      return null;
    }
    var timestamp = Number(value && value.timestamp);
    if (!Number.isFinite(timestamp)) {
      return null;
    }
    return {
      version: 2,
      status: 'confirmed',
      country: value.country,
      language: value.language,
      createdAt: timestamp,
      expiresAt: timestamp + ttl
    };
  };

  var createLegacyDismissalState = function (value) {
    if (!value) {
      return null;
    }
    var timestamp = Number(value && value.timestamp);
    if (!Number.isFinite(timestamp)) {
      return null;
    }
    return {
      version: 2,
      status: 'dismissed',
      country: value.country,
      language: value.language,
      contextCountry: value.currentCountry,
      contextLanguage: value.currentLanguage,
      createdAt: timestamp,
      expiresAt: timestamp + DISMISS_TTL
    };
  };

  var getLegacyPromptCandidates = function () {
    return [
      readStoredValue(localStorageRef, LEGACY_STATE_KEY_V3),
      readStoredValue(localStorageRef, LEGACY_STATE_KEY_V2),
      createLegacyConfirmationState(readStoredValue(localStorageRef, CONFIRMED_KEY), CONFIRM_TTL),
      createLegacyConfirmationState(readStoredValue(localStorageRef, LEGACY_CHOICE_KEY), CONFIRM_TTL),
      createLegacyDismissalState(readStoredValue(localStorageRef, DISMISS_KEY))
    ].filter(Boolean);
  };

  var clearLegacyState = function () {
    var legacyKeys = [
      LEGACY_STATE_KEY_V3,
      LEGACY_STATE_KEY_V2,
      LEGACY_CHOICE_KEY,
      CONFIRMED_KEY,
      DISMISS_KEY
    ];
    for (var i = 0; i < legacyKeys.length; i += 1) {
      if (!removeStoredValue(localStorageRef, legacyKeys[i])) {
        return false;
      }
    }
    return true;
  };

  var initializePromptState = function () {
    if (!storageAvailable) {
      return;
    }

    var now = Date.now();
    var availability = getAvailability();
    var promptResult = readStoredResult(localStorageRef, PROMPT_STATE_KEY);
    if (!storageAvailable) {
      return;
    }

    if (promptResult.status === 'ok') {
      memoryState = migrateLocalizationState({
        state: promptResult.value,
        currentContext: currentContext,
        availability: availability,
        now: now
      });
      if (!memoryState && !removeStoredValue(localStorageRef, PROMPT_STATE_KEY)) {
        return;
      }
    } else if (promptResult.status === 'invalid'
      && !removeStoredValue(localStorageRef, PROMPT_STATE_KEY)) {
      return;
    }

    var candidates = getLegacyPromptCandidates();
    if (!storageAvailable) {
      return;
    }

    if (!memoryState) {
      for (var i = 0; i < candidates.length; i += 1) {
        var migrated = migrateLocalizationState({
          state: candidates[i],
          currentContext: currentContext,
          availability: availability,
          now: now
        });
        if (migrated) {
          if (!persistPromptState(migrated)) {
            return;
          }
          break;
        }
      }
      if (!memoryState) {
        var legacyPendingSuppression = createLegacyPendingSuppression({
          states: candidates,
          currentContext: currentContext,
          availability: availability,
          now: now,
          expiresAt: now + UNRESOLVED_SUPPRESSION_TTL
        });
        if (legacyPendingSuppression && !persistPromptState(legacyPendingSuppression)) {
          return;
        }
      }
    }

    clearLegacyState();
  };

  var reconcileStoredIntent = function () {
    if (!storageAvailable) {
      return;
    }

    var now = Date.now();
    var intentResult = readStoredResult(sessionStorageRef, INTENT_KEY);
    if (!storageAvailable) {
      return;
    }
    var result = reconcileLocalizationIntent({
      intent: intentResult.status === 'ok' ? intentResult.value : null,
      currentContext: currentContext,
      availability: getAvailability(),
      now: now
    });

    if (result.outcome === 'verified') {
      unresolvedIntent = null;
      if (!removeStoredValue(sessionStorageRef, INTENT_KEY)) {
        return;
      }
      var confirmedState = createLocalizationPromptState({
        status: 'confirmed',
        context: currentContext,
        reason: 'verified_intent',
        now: now,
        expiresAt: now + CONFIRM_TTL
      });
      if (confirmedState) {
        persistPromptState(confirmedState);
      }
      return;
    }

    if (result.outcome === 'unresolved') {
      unresolvedIntent = result.intent;
      if (!persistIntentState(result.intent)) {
        return;
      }
      if (!isLocalizationContextCoherent({
        context: currentContext,
        availability: getAvailability()
      })) {
        return;
      }
      var suppressionState = createLocalizationPromptState({
        status: 'dismissed',
        context: currentContext,
        reason: 'unresolved_intent',
        now: now,
        expiresAt: now + UNRESOLVED_SUPPRESSION_TTL
      });
      if (suppressionState) {
        persistPromptState(suppressionState);
      }
      return;
    }

    unresolvedIntent = null;
    if (intentResult.status !== 'empty') {
      removeStoredValue(sessionStorageRef, INTENT_KEY);
    }
  };

  var initializeControllerState = function () {
    if (controllerStateInitialized) {
      return;
    }
    controllerStateInitialized = true;
    if (!storageAvailable) {
      return;
    }
    initializePromptState();
    if (storageAvailable) {
      reconcileStoredIntent();
    }
  };

  var setDismissed = function () {
    if (!storageAvailable || !isLocalizationContextCoherent({
      context: currentContext,
      availability: getAvailability()
    })) {
      return;
    }
    var now = Date.now();
    var state = createLocalizationPromptState({
      status: 'dismissed',
      context: currentContext,
      reason: 'customer_dismissal',
      now: now,
      expiresAt: now + DISMISS_TTL
    });
    if (state) {
      persistPromptState(state);
    }
  };

  var focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var focusFirstControl = function () {
    var target = dialog.querySelector(focusableSelector);
    if (target) {
      target.focus();
    }
  };

  var completePrompt = function (outcome, source) {
    if (!promptCoordinator || promptCompleted) {
      return;
    }
    promptCompleted = true;
    promptCoordinator.complete('localization', {
      outcome: outcome || pendingPromptOutcome || 'closed',
      source: source || activePromptSource
    });
  };

  var finishDialogClose = function () {
    var outcome = pendingPromptOutcome || (openedAsRecommendation ? 'dismissed' : 'manual_closed');
    var source = activePromptSource;
    document.dispatchEvent(new CustomEvent('hdt:localization:closed', {
      bubbles: true
    }));
    document.documentElement.removeAttribute('scroll-lock');
    resetDialogCopy();
    clearLocalizationStatus();
    openedAsRecommendation = false;
    activeRecommendation = null;
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
    if (promptSuspended) {
      promptSuspended = false;
      return;
    }
    completePrompt(outcome, source);
  };

  var closeDialog = function (rememberDismissal) {
    if (rememberDismissal || openedAsRecommendation) {
      setDismissed();
      pendingPromptOutcome = 'dismissed';
    } else if (!pendingPromptOutcome) {
      pendingPromptOutcome = 'manual_closed';
    }
    closeCustomSelects();
    clearLocalizationStatus();
    openedAsRecommendation = false;
    activeRecommendation = null;
    resetDialogCopy();
    if (typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
      finishDialogClose();
    }
    document.documentElement.removeAttribute('scroll-lock');
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
  };

  var openDialog = function (options) {
    options = options || {};
    clearLocalizationStatus();
    ensureCustomOptions();
    pendingPromptOutcome = '';
    promptCompleted = false;
    promptSuspended = false;
    setReturnTo();
    lastFocused = document.activeElement;
    openedAsRecommendation = options.source === 'recommendation';
    activePromptSource = openedAsRecommendation ? 'automatic' : 'manual';
    if (openedAsRecommendation && options.recommendation) {
      activeRecommendation = options.recommendation;
      if (activeRecommendation.type === 'confirmation') {
        setConfirmationCopy(activeRecommendation);
      } else {
        setRecommendationCopy(activeRecommendation);
      }
    } else {
      activeRecommendation = null;
      resetSelectionToCurrent();
      resetDialogCopy();
      showUnresolvedStatus();
    }
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) {
        dialog.showModal();
      }
    } else {
      dialog.setAttribute('open', '');
    }
    document.documentElement.setAttribute('scroll-lock', '');
    window.requestAnimationFrame(focusFirstControl);
  };

  var closeDrawerIfNeeded = function (trigger) {
    var drawer = document.getElementById('CartDrawer');
    var inDrawer = trigger && trigger.closest && (trigger.closest('#CartDrawer') || trigger.closest('hdt-cart-drawer'));
    if (!drawer || !inDrawer) {
      return false;
    }
    var activeDrawerDialog = trigger.closest('dialog[open]');
    if (activeDrawerDialog && activeDrawerDialog !== drawer && typeof activeDrawerDialog.close === 'function') {
      activeDrawerDialog.close();
    }
    var closeButton = drawer.querySelector('button[aria-controls="CartDrawer"]');
    if (closeButton) {
      closeButton.click();
      return true;
    }
    if (typeof drawer.close === 'function') {
      drawer.close();
      return true;
    }
    return false;
  };

  var getPreferredLanguageForCountry = function (countryCode) {
    var pathLanguage = getLanguageFromPath();
    var languages = getLanguagesForCountry(countryCode);
    var browserLanguage = getBrowserLanguageMatch(languages);

    if (pathLanguage && hasLanguage(languages, pathLanguage)) {
      return findLanguage(languages, pathLanguage);
    }

    if (browserLanguage) {
      return browserLanguage;
    }

    return findLanguage(languages, config.currentLanguage) || languages[0] || null;
  };

  var applySuggestedCountry = function (countryCode) {
    var suggestedCountry = findCountry(countryCode);
    if (!suggestedCountry || !countrySelect) {
      return false;
    }
    var pathLanguage = getLanguageFromPath();
    var languages = getLanguagesForCountry(suggestedCountry.code);
    var preferredLanguage = pathLanguage && hasLanguage(languages, pathLanguage) ? pathLanguage : config.currentLanguage;

    countrySelect.value = suggestedCountry.code;
    updateCountryFlag(suggestedCountry.code);
    updateCustomCountry(suggestedCountry.code);
    updateLanguageOptions(suggestedCountry.code, preferredLanguage);
    return true;
  };

  var applyConfirmationSelection = function (state) {
    if (state.country) {
      applySuggestedCountry(state.country.code);
    }

    if (state.suggestedLanguage) {
      applySuggestedLanguage(state.suggestedLanguage);
    }
  };

  var applySuggestedLanguage = function (languageCode) {
    if (!languageSelect || !languageCode) {
      return false;
    }

    var countryCode = countrySelect ? countrySelect.value : config.currentCountry;
    var languages = getLanguagesForCountry(countryCode);
    if (!hasLanguage(languages, languageCode)) {
      return false;
    }

    updateLanguageOptions(countryCode, languageCode);
    return true;
  };

  var resetSelectionToCurrent = function () {
    if (!countrySelect) {
      return;
    }

    countrySelect.value = config.currentCountry || countrySelect.value;
    updateCountryFlag(countrySelect.value);
    updateCustomCountry(countrySelect.value);
    updateLanguageOptions(countrySelect.value, config.currentLanguage);
  };

  var closeCustomSelects = function (except) {
    root.querySelectorAll('[data-localization-custom]').forEach(function (custom) {
      if (except && custom === except) {
        return;
      }
      var parts = getCustomParts(custom);
      custom.classList.remove('is-open');
      if (parts.trigger) {
        parts.trigger.setAttribute('aria-expanded', 'false');
      }
      if (parts.list) {
        parts.list.hidden = true;
      }
    });
  };

  var focusSelectedCustomOption = function (custom) {
    var selected = custom ? custom.querySelector('[data-localization-option][aria-selected="true"]') : null;
    var first = custom ? custom.querySelector('[data-localization-option]') : null;
    var target = selected || first;
    if (target) {
      target.focus();
    }
  };

  var openCustomSelect = function (custom, focusOption) {
    if (!custom) {
      return;
    }

    ensureCustomOptions();

    var parts = getCustomParts(custom);
    closeCustomSelects(custom);
    custom.classList.add('is-open');
    if (parts.trigger) {
      parts.trigger.setAttribute('aria-expanded', 'true');
    }
    if (parts.list) {
      parts.list.hidden = false;
    }
    if (focusOption) {
      window.requestAnimationFrame(function () {
        focusSelectedCustomOption(custom);
      });
    }
  };

  var toggleCustomSelect = function (custom) {
    if (custom && custom.classList.contains('is-open')) {
      closeCustomSelects();
      return;
    }
    openCustomSelect(custom, false);
  };

  var selectCustomOption = function (option) {
    var countryCode = option.getAttribute('data-localization-country-option');
    var languageCode = option.getAttribute('data-localization-language-option');
    var custom = option.closest('[data-localization-custom]');
    var parts = getCustomParts(custom);

    if (countryCode && countrySelect) {
      countrySelect.value = countryCode;
      countrySelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (languageCode && languageSelect) {
      languageSelect.value = languageCode;
      updateCustomLanguage(languageCode);
    }

    closeCustomSelects();
    if (parts.trigger) {
      parts.trigger.focus();
    }
  };

  var moveCustomFocus = function (current, direction) {
    var custom = current.closest('[data-localization-custom]');
    var options = Array.prototype.slice.call(custom.querySelectorAll('[data-localization-option]'));
    var index = options.indexOf(current);
    var nextIndex = Math.max(0, Math.min(options.length - 1, index + direction));
    if (options[nextIndex]) {
      options[nextIndex].focus();
    }
  };

  var getAvailability = function () {
    return countries.reduce(function (availability, country) {
      availability[normalizeCountryCode(country.code)] = getLanguagesForCountry(country.code).map(function (language) {
        return normalizeLanguageCode(language.code);
      });
      return availability;
    }, {});
  };

  var getSuggestionParts = function (data) {
    var suggestions = data && Array.isArray(data.suggestions) ? data.suggestions : [];
    var first = suggestions[0] && suggestions[0].parts ? suggestions[0].parts : {};
    return {
      country: first.country && first.country.handle ? normalizeCountryCode(first.country.handle) : '',
      language: first.language && first.language.handle ? normalizeLanguageCode(first.language.handle) : ''
    };
  };

  var buildConfirmationState = function (data) {
    var currentCountry = normalizeCountryCode(config.currentCountry);
    var currentLanguage = normalizeLanguageCode(config.currentLanguage);
    var suggestions = getSuggestionParts(data);
    var detected = data && data.detected_values && data.detected_values.country;
    var detectedCountryCode = normalizeCountryCode(detected && (detected.handle || detected.iso_code || detected.code));
    var detectedCountry = findCountry(detectedCountryCode);
    var browserLanguage = getBrowserLanguageMatch(allLanguages);
    var selection = getLocalizationSelection({
      pathLanguage: getLanguageFromPath(),
      suggestedCountry: suggestions.country,
      detectedCountry: detectedCountryCode,
      browserLanguage: suggestions.language || (browserLanguage ? browserLanguage.code : ''),
      currentCountry: currentCountry,
      currentLanguage: currentLanguage,
      countries: getAvailability()
    });
    var selectedCountry = findCountry(selection.country) || detectedCountry || findCountry(currentCountry);
    var selectedCountryCode = selectedCountry ? selectedCountry.code : currentCountry;
    var selectedLanguages = getLanguagesForCountry(selectedCountryCode);
    var preferredLanguage = findLanguage(selectedLanguages, selection.language) || getPreferredLanguageForCountry(selectedCountryCode);
    var suggestedLanguage = preferredLanguage ? normalizeLanguageCode(preferredLanguage.code) : currentLanguage;

    return {
      type: 'confirmation',
      detectedCountry: detectedCountry ? normalizeCountryCode(detectedCountry.code) : '',
      currentCountry: currentCountry,
      suggestedLanguage: suggestedLanguage,
      currentLanguage: currentLanguage,
      country: selectedCountry || null,
      language: preferredLanguage || null
    };
  };

  var maybeConfirmLocalization = function () {
    if (window.Shopify && window.Shopify.designMode) {
      completePrompt('skipped', 'automatic');
      return;
    }

    initializeControllerState();
    if (!storageAvailable) {
      completePrompt('skipped', 'automatic');
      return;
    }

    var availability = getAvailability();
    if (!isLocalizationContextCoherent({
      context: currentContext,
      availability: availability
    })) {
      completePrompt('skipped', 'automatic');
      return;
    }

    var decision = getLocalizationDecision({
      state: memoryState,
      storageAvailable: storageAvailable,
      currentContext: currentContext,
      availability: availability,
      now: Date.now()
    });

    if (!decision.eligible) {
      completePrompt('skipped', 'automatic');
      return;
    }

    var routesRoot = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';

    var queueConfirmation = function (data) {
      if (manualInteractionStarted) return;
      var recommendationState = buildConfirmationState(data);
      applyConfirmationSelection(recommendationState);

      if (!promptCoordinator) {
        openDialog({ source: 'recommendation', recommendation: recommendationState });
        return;
      }

      promptCoordinator.request({
        id: 'localization',
        priority: 20,
        source: 'automatic',
        isOpen: function () { return dialog.open; },
        minVisibleMs: 0,
        pause: function () {
          if (!dialog.open) return;
          promptSuspended = true;
          pendingPromptOutcome = 'interrupted';
          openedAsRecommendation = false;
          closeDialog(false);
        },
        open: function () {
          openDialog({ source: 'recommendation', recommendation: recommendationState });
        }
      });
    };

    fetch(routesRoot + 'browsing_context_suggestions.json', { method: 'GET' })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (data) {
        queueConfirmation(data);
      })
      .catch(function () {
        queueConfirmation(null);
      });
  };

  if (countrySelect) {
    updateCountryFlag(countrySelect.value);
    updateCustomCountry(countrySelect.value);
    updateLanguageOptions(countrySelect.value, config.currentLanguage);
    countrySelect.addEventListener('change', function () {
      updateCountryFlag(countrySelect.value);
      updateCustomCountry(countrySelect.value);
      updateLanguageOptions(countrySelect.value, languageSelect ? languageSelect.value : config.currentLanguage);
    });
  }

  if (languageSelect) {
    languageSelect.addEventListener('change', function () {
      updateCustomLanguage(languageSelect.value);
    });
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) {
      return;
    }

    var customTrigger = target.closest('[data-localization-custom-trigger]');
    if (customTrigger && root.contains(customTrigger)) {
      event.preventDefault();
      toggleCustomSelect(customTrigger.closest('[data-localization-custom]'));
      return;
    }

    var customOption = target.closest('[data-localization-option]');
    if (customOption && root.contains(customOption)) {
      event.preventDefault();
      selectCustomOption(customOption);
      return;
    }

    if (!target.closest('[data-localization-custom]')) {
      closeCustomSelects();
    }

    var trigger = target.closest('[data-open-localization-selector], .js-open-country-selector');
    if (trigger) {
      if (root.dataset.localizationControllerReady !== 'true') return;
      event.preventDefault();
      var wasDrawerClosed = closeDrawerIfNeeded(trigger);
      window.setTimeout(function () {
        document.dispatchEvent(new CustomEvent('hdt:localization:open', {
          bubbles: true,
          detail: { source: trigger.classList.contains('js-open-country-selector') ? 'quote' : 'manual' }
        }));
      }, wasDrawerClosed ? 350 : 0);
      return;
    }

    if (target.closest('[data-localization-close]')) {
      event.preventDefault();
      closeDialog(false);
      return;
    }

    if (target.closest('[data-localization-dismiss]')) {
      event.preventDefault();
      closeDialog(false);
    }
  });

  document.addEventListener('hdt:localization:open', function (event) {
    manualInteractionStarted = true;
    var options = event.detail || { source: 'manual' };
    if (!promptCoordinator) {
      openDialog(options);
      return;
    }
    promptCoordinator.requestManual({
      id: 'localization',
      isOpen: function () { return dialog.open; },
      fallback: function () { window.location.hash = 'HdtLocalizationFallback'; },
      open: function () {
        openDialog(options);
      }
    });
  });

  dialog.addEventListener('cancel', function (event) {
    if (root.querySelector('[data-localization-custom].is-open')) {
      event.preventDefault();
      closeCustomSelects();
      return;
    }
    if (openedAsRecommendation) {
      setDismissed();
      pendingPromptOutcome = 'dismissed';
    } else {
      pendingPromptOutcome = 'manual_closed';
    }
  });

  dialog.addEventListener('click', function (event) {
    if (event.target === dialog) {
      closeDialog(false);
    }
  });

  dialog.addEventListener('close', finishDialogClose);

  dialog.addEventListener('keydown', function (event) {
    var customTrigger = event.target.closest && event.target.closest('[data-localization-custom-trigger]');
    var customOption = event.target.closest && event.target.closest('[data-localization-option]');

    if (customTrigger && root.contains(customTrigger)) {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
        event.preventDefault();
        openCustomSelect(customTrigger.closest('[data-localization-custom]'), true);
        return;
      }
      if (event.key === 'Escape') {
        closeCustomSelects();
        return;
      }
    }

    if (customOption && root.contains(customOption)) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        selectCustomOption(customOption);
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        moveCustomFocus(customOption, 1);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        moveCustomFocus(customOption, -1);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        closeCustomSelects();
        var parentCustom = customOption.closest('[data-localization-custom]');
        var parts = getCustomParts(parentCustom);
        if (parts.trigger) {
          parts.trigger.focus();
        }
        return;
      }
    }

    if (event.key !== 'Tab') {
      return;
    }
    var focusable = Array.prototype.slice.call(dialog.querySelectorAll(focusableSelector)).filter(isVisible);
    if (!focusable.length) {
      return;
    }
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  form.addEventListener('submit', function (event) {
    setReturnTo();
    var selectedCountry = findCountry(countrySelect ? countrySelect.value : '');
    var selectionAlreadyApplied = isLocalizationSelectionAlreadyApplied({
      selection: {
        country: selectedCountry ? selectedCountry.code : '',
        language: languageSelect ? languageSelect.value : '',
        currency: selectedCountry ? selectedCountry.currency : ''
      },
      currentContext: currentContext,
      availability: getAvailability()
    });

    if (selectionAlreadyApplied) {
      event.preventDefault();
      var now = Date.now();
      unresolvedIntent = null;
      if (storageAvailable) {
        removeStoredValue(sessionStorageRef, INTENT_KEY);
        var confirmedState = createLocalizationPromptState({
          status: 'confirmed',
          context: currentContext,
          reason: 'confirmed_unchanged',
          now: now,
          expiresAt: now + CONFIRM_TTL
        });
        if (confirmedState && storageAvailable) {
          persistPromptState(confirmedState);
        }
      }
      pendingPromptOutcome = 'confirmed_unchanged';
      openedAsRecommendation = false;
      closeDialog(false);
      completePrompt('confirmed_unchanged', activePromptSource);
      return;
    }

    var intent = createLocalizationIntent({
      country: selectedCountry ? selectedCountry.code : '',
      language: languageSelect ? languageSelect.value : '',
      currency: selectedCountry ? selectedCountry.currency : '',
      sourceContext: currentContext,
      now: Date.now(),
      expiresAt: Date.now() + INTENT_TTL
    });
    if (intent && storageAvailable && !writeJsonState(sessionStorageRef, INTENT_KEY, intent)) {
      enterManualOnly();
    }
    pendingPromptOutcome = 'changed';
    completePrompt('changed', activePromptSource);
  });

  root.dataset.localizationControllerReady = 'true';

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', maybeConfirmLocalization, { once: true });
  } else {
    maybeConfirmLocalization();
  }
})();
